import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { WHOAMI } from "../graphql/user";
import { useRouter } from "next/router";
import { setCookie, getCookie } from 'cookies-next';
import LoadingShell from "components/LoadingShell/LoadingShell";

const UserInfoContext = React.createContext({
  currentUser: null,
  currentUserId: null,
  role: null,
});

export function useUserInfo() {
  return useContext(UserInfoContext);
}

export function UserInfoProvider({ children, onReady }) {
  const { data, loading, error, refetch } = useQuery(WHOAMI, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only'
  });

  const router = useRouter();
  const hasAttemptedSync = useRef(false);
  const [infrastructureError, setInfrastructureError] = useState(false);

  useEffect(() => {
    // Still loading WHOAMI, do nothing yet
    if (loading) return;

    // WHOAMI succeeded, clear any infrastructure error and signal ready
    if (data?.whoami) {
      hasAttemptedSync.current = false;
      setInfrastructureError(false);
      if (onReady) onReady();
      return;
    }

    // Infrastructure error (502, 503, 504) — backend is down, NOT an auth failure.
    // Do NOT redirect to login or clear tokens. Show a "service unavailable" state
    // and let the user retry. This prevents a backend restart from logging out
    // every active user.
    const statusCode = error?.networkError?.statusCode;
    if (statusCode && statusCode >= 500) {
      console.error(`[UserInfoContext] Infrastructure error (HTTP ${statusCode}), not treating as auth failure`);
      setInfrastructureError(true);
      return;
    }

    // Amplify v6 cookie-based session check.
    //
    // Background: this code originally targeted Amplify v4/v5 which stored
    // tokens in localStorage under `CognitoIdentityServiceProvider.{clientId}.LastAuthUser`.
    // Amplify v6 with `ssr: true` switched to `CookieStorage` and stopped writing
    // localStorage entirely. The v6 cookie naming pattern is
    // `CognitoIdentityServiceProvider.{clientId}.{username}.{tokenType}` — there
    // is NO standalone `LastAuthUser` cookie.
    //
    // Without this check, when WHOAMI fails on a v6 session (e.g. transient
    // backend slowness, schema drift, GraphQL parse error) the legacy code
    // below falls through to `router.replace("/")`. Since `getServerSideAuth`
    // sees the still-valid Cognito cookies and redirects `/` back to
    // `/valuation/list`, the SSR redirects ping-pong forever in a tight
    // client-side data-fetch loop. This was the root cause of issue #562.
    if (typeof document !== 'undefined') {
      const v6CookiePrefix = `CognitoIdentityServiceProvider.${process.env.USER_POOL_CLIENT_ID}.`;
      const hasV6Session = document.cookie
        .split(';')
        .some((c) => {
          const trimmed = c.trim();
          // Match `<prefix><username>.accessToken` etc., but NOT the bare `<prefix>LastAuthUser`
          return trimmed.startsWith(v6CookiePrefix) &&
            (trimmed.includes('.accessToken=') || trimmed.includes('.idToken='));
        });
      if (hasV6Session) {
        console.error(
          '[UserInfoContext] WHOAMI failed but Amplify v6 session cookies are present —',
          'treating as transient backend issue rather than auth failure to avoid SSR redirect loop.',
          { error }
        );
        setInfrastructureError(true);
        return;
      }
    }

    // Legacy Amplify v4/v5 fallback: try to sync tokens from localStorage to cookies once.
    // Kept as a defensive measure for any user with stale localStorage entries from
    // before the v6 migration. Should be removable once we're confident no v4/v5
    // sessions remain in the wild.
    const cookieLastAuthUser = getCookie(`CognitoIdentityServiceProvider.${process.env.USER_POOL_CLIENT_ID}.LastAuthUser`);
    if (cookieLastAuthUser && hasAttemptedSync.current) {
      // Cookies exist but query still failing after sync attempt - redirect to login instead of infinite loop
      localStorage.clear();
      router.replace("/");
      return;
    }

    const lastAuthUser = localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.USER_POOL_CLIENT_ID}.LastAuthUser`
    );

    // No user in localStorage → clear and go back to login
    if (!lastAuthUser) {
      localStorage.clear();
      if (router.asPath !== "/") {
        router.replace("/");
      }
      return;
    }

    const refreshToken = localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.USER_POOL_CLIENT_ID}.${lastAuthUser}.refreshToken`
    );
    const accessToken = localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.USER_POOL_CLIENT_ID}.${lastAuthUser}.accessToken`
    );
    const idToken = localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.USER_POOL_CLIENT_ID}.${lastAuthUser}.idToken`
    );

    // We have tokens in localStorage
    if (refreshToken && accessToken && idToken) {
      // Already tried syncing once and WHOAMI still failed → give up and log out
      if (hasAttemptedSync.current) {
        localStorage.clear();
        router.replace("/");
        return;
      }

      // First time: copy tokens to cookies and refetch WHOAMI (no hard reload)
      setCookie(
        `CognitoIdentityServiceProvider.${process.env.USER_POOL_CLIENT_ID}.LastAuthUser`,
        lastAuthUser
      );
      setCookie(
        `CognitoIdentityServiceProvider.${process.env.USER_POOL_CLIENT_ID}.${lastAuthUser}.refreshToken`,
        refreshToken
      );
      setCookie(
        `CognitoIdentityServiceProvider.${process.env.USER_POOL_CLIENT_ID}.${lastAuthUser}.accessToken`,
        accessToken
      );
      setCookie(
        `CognitoIdentityServiceProvider.${process.env.USER_POOL_CLIENT_ID}.${lastAuthUser}.idToken`,
        idToken
      );

      hasAttemptedSync.current = true;
      refetch();
      return;
    }

    // Tokens incomplete → clear and go back to login
    localStorage.clear();
    router.replace("/");
  }, [data, loading, refetch, router]);

  const whoami = data?.whoami || null;

  // Memoised on `whoami` alone. Both of these used to be rebuilt on every
  // render of this provider, and for a super admin `currentUser` was a brand
  // new object each time. Every consumer re-rendered, and every downstream
  // useMemo/useEffect keyed on `currentUser` was invalidated — including the
  // valuation page's mobile header actions, which then wrote back into
  // MobileHeaderContext and re-rendered the whole tree again.
  const value = useMemo(() => {
    const effectiveRole = whoami?.superAdmin ? "SUPER_ADMIN" : whoami?.role;
    const effectiveUser =
      whoami && whoami.superAdmin ? { ...whoami, role: "SUPER_ADMIN" } : whoami;

    return {
      currentUser: effectiveUser,
      currentUserId: whoami?.businessSubjectId || null,
      role: effectiveRole || null,
      originalRole: whoami?.role || null,
      // Super admins may act as a bank / company / organisation user with their own login; while
      // they do, `role` and businessSubject* above already describe that user (superAdmin is false).
      canActAs: !!whoami?.canActAs,
      actingAs: whoami?.actingAs || null,
    };
  }, [whoami]);

  return (
    <UserInfoContext.Provider value={value}>
      {!loading && data?.whoami ? (
        children
      ) : infrastructureError ? (
        <div className="flex flex-col items-center justify-center min-h-screen">
          <p className="text-lg text-foreground mb-4">Service temporarily unavailable</p>
          <p className="text-sm text-muted-foreground mb-6">The server is not responding. Your session is preserved.</p>
          <button
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary-hover"
            onClick={() => {
              setInfrastructureError(false);
              refetch();
            }}
          >
            Retry
          </button>
        </div>
      ) : (
        <LoadingShell />
      )}
    </UserInfoContext.Provider>
  );
}
